import React, { useState, useEffect } from 'react';
import OrganizationLocationSelector from './OrganizationLocationSelector';
import StatesMultiSelect from './StatesMultiSelect';
import URLParameterBuilder from './URLParameterBuilder';
import {
  type VoucherTemplate,
  type CreateVoucherTemplateDTO,
  type OrganizationAssociation,
  type ItemType,
  type Subcategory,
  type GenerationMode,
  type URLParameters,
  ITEM_TYPE_OPTIONS,
  SUBCATEGORY_OPTIONS,
  GENERATION_MODE_OPTIONS,
} from '../../types/voucherTemplate.types';

interface Props {
  template?: VoucherTemplate | null;
  onSubmit: (data: CreateVoucherTemplateDTO) => void;
  onCancel: () => void;
  loading?: boolean;
}

const VoucherTemplateForm: React.FC<Props> = ({ template, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    voucher_name: '',
    item_type: 'voucher' as ItemType,
    subcategory: 'general' as Subcategory,
    generation_mode: 'static_url' as GenerationMode,
    organizations: [] as OrganizationAssociation[],
    affid: '',
    retailer_promo_code: '',
    alt_price: '',
    voucher_promo_code: '',
    discount_value: '',
    states: [] as string[],
    url_template: '',
    url_parameters: {} as URLParameters,
    qr_code_url: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load template data if editing
  useEffect(() => {
    if (template) {
      setFormData({
        voucher_name: template.voucher_name,
        item_type: template.item_type || 'voucher',
        subcategory: template.subcategory || 'general',
        generation_mode: template.generation_mode || 'static_url',
        organizations: template.organizations,
        affid: template.affid || '',
        retailer_promo_code: template.retailer_promo_code || '',
        alt_price: template.alt_price ? template.alt_price.toString() : '',
        voucher_promo_code: template.voucher_promo_code,
        discount_value: template.discount_value.toString(),
        states: template.states,
        url_template: template.url_template || '',
        url_parameters: template.url_parameters || {},
        qr_code_url: template.qr_code_url || '',
      });
    }
  }, [template]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.voucher_name.trim()) {
      newErrors.voucher_name = 'Voucher name is required';
    }

    if (!formData.voucher_promo_code.trim()) {
      newErrors.voucher_promo_code = 'Voucher promo code is required';
    }

    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }

    if (formData.alt_price && parseFloat(formData.alt_price) <= 0) {
      newErrors.alt_price = 'Alt price must be greater than 0';
    }

    // Validate URL template and parameters for static_url or both modes
    if (formData.generation_mode === 'static_url' || formData.generation_mode === 'both') {
      if (!formData.url_template.trim()) {
        newErrors.url_template = 'URL template is required for this generation mode';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: CreateVoucherTemplateDTO = {
      voucher_name: formData.voucher_name.trim(),
      item_type: formData.item_type,
      subcategory: formData.subcategory,
      generation_mode: formData.generation_mode,
      organizations: formData.organizations,
      affid: formData.affid.trim() || undefined,
      retailer_promo_code: formData.retailer_promo_code.trim() || undefined,
      alt_price: formData.alt_price ? parseFloat(formData.alt_price) : undefined,
      voucher_promo_code: formData.voucher_promo_code.trim(),
      discount_value: parseFloat(formData.discount_value),
      states: formData.states,
      url_template: formData.url_template.trim() || undefined,
      url_parameters: Object.keys(formData.url_parameters).length > 0 ? formData.url_parameters : undefined,
      qr_code_url: formData.qr_code_url.trim() || undefined,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Voucher Name */}
      <div>
        <label htmlFor="voucher_name" className="block text-sm font-medium text-gray-700 mb-1">
          Voucher Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="voucher_name"
          value={formData.voucher_name}
          onChange={(e) => handleChange('voucher_name', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            errors.voucher_name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Birthday Voucher 2025"
        />
        {errors.voucher_name && <p className="mt-1 text-sm text-red-600">{errors.voucher_name}</p>}
      </div>

      {/* Item Type and Subcategory */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="item_type" className="block text-sm font-medium text-gray-700 mb-1">
            Item Type <span className="text-red-500">*</span>
          </label>
          <select
            id="item_type"
            value={formData.item_type}
            onChange={(e) => handleChange('item_type', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {ITEM_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} - {option.description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700 mb-1">
            Subcategory
          </label>
          <select
            id="subcategory"
            value={formData.subcategory || ''}
            onChange={(e) => handleChange('subcategory', e.target.value || null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">None</option>
            {SUBCATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value || ''}>
                {option.label} - {option.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Generation Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Generation Mode <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {GENERATION_MODE_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-start gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="generation_mode"
                value={option.value}
                checked={formData.generation_mode === option.value}
                onChange={(e) => handleChange('generation_mode', e.target.value)}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-500">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* URL Parameter Builder - Show for static_url or both modes */}
      {(formData.generation_mode === 'static_url' || formData.generation_mode === 'both') && (
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900 mb-3">URL Configuration</h3>
          <URLParameterBuilder
            urlTemplate={formData.url_template}
            parameters={formData.url_parameters}
            onChange={(urlTemplate, parameters) => {
              handleChange('url_template', urlTemplate);
              handleChange('url_parameters', parameters);
            }}
          />
          {errors.url_template && <p className="mt-2 text-sm text-red-600">{errors.url_template}</p>}
        </div>
      )}

      {/* Organizations */}
      <OrganizationLocationSelector
        selected={formData.organizations}
        onChange={(orgs) => handleChange('organizations', orgs)}
      />

      {/* States */}
      <StatesMultiSelect
        selected={formData.states}
        onChange={(states) => handleChange('states', states)}
      />

      {/* Two-column grid for smaller fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Voucher Promo Code */}
        <div>
          <label htmlFor="voucher_promo_code" className="block text-sm font-medium text-gray-700 mb-1">
            Voucher Promo Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="voucher_promo_code"
            value={formData.voucher_promo_code}
            onChange={(e) => handleChange('voucher_promo_code', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.voucher_promo_code ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="BDAY2025"
          />
          <p className="mt-1 text-xs text-gray-500">Accounting system identifier</p>
          {errors.voucher_promo_code && (
            <p className="mt-1 text-sm text-red-600">{errors.voucher_promo_code}</p>
          )}
        </div>

        {/* Discount Value */}
        <div>
          <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-1">
            Discount Value ($) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="discount_value"
            value={formData.discount_value}
            onChange={(e) => handleChange('discount_value', e.target.value)}
            step="0.01"
            min="0.01"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.discount_value ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="50.00"
          />
          {errors.discount_value && <p className="mt-1 text-sm text-red-600">{errors.discount_value}</p>}
        </div>
      </div>

      {/* Three-column grid for legacy/optional fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Affid */}
        <div>
          <label htmlFor="affid" className="block text-sm font-medium text-gray-700 mb-1">
            Affid (Legacy)
          </label>
          <input
            type="text"
            id="affid"
            value={formData.affid}
            onChange={(e) => handleChange('affid', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Optional"
          />
        </div>

        {/* Retailer Promo Code */}
        <div>
          <label htmlFor="retailer_promo_code" className="block text-sm font-medium text-gray-700 mb-1">
            Retailer Promo Code
          </label>
          <input
            type="text"
            id="retailer_promo_code"
            value={formData.retailer_promo_code}
            onChange={(e) => handleChange('retailer_promo_code', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Optional"
          />
        </div>

        {/* Alt Price */}
        <div>
          <label htmlFor="alt_price" className="block text-sm font-medium text-gray-700 mb-1">
            Alt Price ($)
          </label>
          <input
            type="number"
            id="alt_price"
            value={formData.alt_price}
            onChange={(e) => handleChange('alt_price', e.target.value)}
            step="0.01"
            min="0.01"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.alt_price ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Price test parameter"
          />
          {errors.alt_price && <p className="mt-1 text-sm text-red-600">{errors.alt_price}</p>}
        </div>
      </div>

      {/* QR Code URL */}
      <div>
        <label htmlFor="qr_code_url" className="block text-sm font-medium text-gray-700 mb-1">
          QR Code URL
        </label>
        <input
          type="url"
          id="qr_code_url"
          value={formData.qr_code_url}
          onChange={(e) => handleChange('qr_code_url', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="https://signup.uslawshield.com/..."
        />
        <p className="mt-1 text-xs text-gray-500">
          URL embedded in QR code (leads to signup/action page where voucher instances will be created)
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          {template ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </form>
  );
};

export default VoucherTemplateForm;
